import type { FC, ReactNode, RefObject } from 'react'
import type { PathOption } from '@/constants/content'

import { useEffect, useRef, useState } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'

import { ArrowRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useReduceMotion } from '@/hooks/use-reduce-motion'
import { arrowClass, buttonClass } from '@/components/generals/button'
import QuoteLink from '@/components/generals/quote-link'
import SmartLink from '@/components/generals/smart-link'
import { LaunchMotif, ScaleMotif } from '@/components/landing/path-motifs'
import { paths } from '@/constants/content'

type Side = PathOption['id']

type ComparisonRowData = (typeof paths.comparison)[number]

interface PathCardProps {
    option: PathOption
    index: number
    cardRef: RefObject<HTMLElement | null>
    hovered: Side | null
    onHover: (side: Side | null) => void
    className?: string
}

interface ComparisonRowProps {
    row: ComparisonRowData
    index: number
    first?: boolean
}

interface ForkGeometry {
    width: number
    left: number
    right: number
}

const FORK_HEIGHT = 128
const FORK_END = FORK_HEIGHT - 12
const SEAM = 'lg:grid-cols-[minmax(0,1fr)_11rem_minmax(0,1fr)]'

const PathCard: FC<PathCardProps> = ({ option, index, cardRef, hovered, onHover, className }): ReactNode => {
    const isBlaze = option.tone === 'blaze'
    const active = hovered === option.id
    const dimmed = hovered !== null && !active

    return (
        <article
            ref={cardRef}
            aria-labelledby={`path-${option.id}`}
            onPointerEnter={(event) => {
                if (event.pointerType === 'mouse') onHover(option.id)
            }}
            onPointerLeave={(event) => {
                if (event.pointerType === 'mouse') onHover(null)
            }}
            onFocus={() => onHover(option.id)}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onHover(null)
            }}
            className={cn(
                'relative flex flex-col overflow-hidden rounded-lg border-2 border-ink p-6 transition-opacity duration-500 ease-snap sm:p-8 lg:min-h-[40rem] lg:rounded-b-none lg:p-10',
                isBlaze ? 'bg-blaze text-ink' : 'bg-[#21cecd] text-ink',
                dimmed && 'opacity-85',
                className
            )}
        >
            <div
                aria-hidden='true'
                className={cn(
                    'pointer-events-none absolute inset-0 transition-[background-position,opacity,scale] duration-700 ease-snap',
                    isBlaze
                        ? 'opacity-[0.05] [background-image:repeating-linear-gradient(115deg,var(--color-ink)_0_2px,transparent_2px_26px)] [background-size:200%_100%]'
                        : 'opacity-[0.07] [background-image:linear-gradient(var(--color-ink)_1px,transparent_1px),linear-gradient(90deg,var(--color-ink)_1px,transparent_1px)] [background-size:36px_36px]',
                    isBlaze && (active ? '[background-position:-140px_0] opacity-[0.08]' : '[background-position:0_0]'),
                    !isBlaze && active && 'scale-110 opacity-[0.12]'
                )}
            />

            <div className='relative flex items-start justify-between gap-6'>
                <span className='eyebrow'>
                    {String(index).padStart(2, '0')}
                </span>

                {isBlaze ? <LaunchMotif active={active} /> : <ScaleMotif active={active} />}
            </div>

            <div className='relative mt-14 lg:mt-auto lg:pt-16'>
                <h3
                    id={`path-${option.id}`}
                    className='font-heading text-[3.25rem] font-extrabold leading-[0.88] tracking-[-0.04em] transition-[font-variation-settings] duration-500 ease-snap sm:text-7xl lg:text-[clamp(3rem,4.6vw,4.75rem)]'
                    style={{ fontVariationSettings: active ? '"wdth" 92' : '"wdth" 75' }}
                >
                    {option.title}
                </h3>

                <p className='mt-5 font-heading text-xl font-semibold leading-snug tracking-[-0.01em] sm:text-2xl'>
                    {option.tagline}
                </p>

                <p className='mt-4 max-w-[32rem] text-base leading-relaxed sm:text-lg'>
                    {option.body}
                </p>
            </div>

            <div className={cn('relative mt-8 flex flex-wrap items-end justify-between gap-5 border-t-2 pt-6', isBlaze ? 'border-ink/20' : 'border-paper/25')}>
                <p className='leading-none'>
                    <span className='eyebrow block'>
                        {option.priceLabel}
                    </span>

                    {' '}

                    <span className='mt-2 block font-heading text-5xl font-extrabold tracking-[-0.03em] wdth-80 sm:text-6xl'>
                        {option.price}
                    </span>
                </p>

                <QuoteLink
                    path={option.id}
                    className={buttonClass({
                        variant: isBlaze ? 'ink' : 'paper',
                        shadow: isBlaze ? 'paper' : 'ink'
                    })}
                >
                    {option.cta}
                    <ArrowRight aria-hidden='true' className={arrowClass} />
                </QuoteLink>
            </div>
        </article>
    )
}

// Each row slides in from both sides as it scrolls into view.
const ComparisonRow: FC<ComparisonRowProps> = ({ row, index, first = false }): ReactNode => {
    const reduce = useReduceMotion()
    const reveal = (fromLeft: boolean) => ({
        hidden: { x: fromLeft ? -28 : 28, opacity: 0 },
        shown: { x: 0, opacity: 1, transition: { delay: Math.min(index, 2) * 0.05, duration: 0.55, ease: [0.22, 1, 0.36, 1] as const } }
    })
    const motionProps = {
        initial: reduce ? false : 'hidden',
        whileInView: 'shown',
        viewport: { once: true, amount: 0.3 }
    } as const

    const cell = 'px-4 pb-5 pt-3 text-[0.9375rem] font-semibold leading-snug sm:px-6 sm:text-base lg:px-10 lg:py-5 lg:text-lg'
    const divider = first ? '' : 'lg:border-t lg:border-t-ink/15'

    return (
        <div className={cn('grid grid-cols-2', SEAM)}>
            <p
                className={cn(
                    'eyebrow relative col-span-2 flex justify-center px-4 pt-4 text-center text-ink/80',
                    'bg-[linear-gradient(to_right,var(--color-blaze-soft)_50%,var(--color-cobalt-soft)_50%)]',
                    'before:absolute before:inset-y-0 before:left-1/2 before:w-[2px] before:bg-ink',
                    !first && 'border-t border-t-ink/15',
                    'lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:items-center lg:border-t-0 lg:bg-none lg:px-3 lg:py-5 lg:text-ink/70 lg:before:hidden'
                )}
            >
                <span className='relative rounded-full border-2 border-ink bg-paper px-3 py-1 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0'>
                    {row.label}
                </span>
            </p>

            <motion.p
                {...motionProps}
                variants={reveal(true)}
                className={cn(cell, divider, 'bg-blaze-soft lg:col-start-1 lg:row-start-1 lg:border-x-2 lg:border-x-ink lg:text-right')}
            >
                <span className='sr-only'>{`${paths.options[0].title}: `}</span>
                {row.launch}
            </motion.p>

            <motion.p
                {...motionProps}
                variants={reveal(false)}
                className={cn(cell, divider, 'border-l-2 border-ink bg-cobalt-soft lg:col-start-3 lg:row-start-1 lg:border-x-2 lg:border-x-ink')}
            >
                <span className='sr-only'>{`${paths.options[1].title}: `}</span>
                {row.scale}
            </motion.p>
        </div>
    )
}

const Paths: FC = (): ReactNode => {
    const headingRef = useRef<HTMLHeadingElement>(null)
    const forkRef = useRef<HTMLDivElement>(null)
    const launchRef = useRef<HTMLElement>(null)
    const scaleRef = useRef<HTMLElement>(null)
    const reduce = useReduceMotion()
    const [hovered, setHovered] = useState<Side | null>(null)
    const [fork, setFork] = useState<ForkGeometry | null>(null)

    const { scrollYProgress: headingProgress } = useScroll({ target: headingRef, offset: ['start end', 'start 40%'] })
    const firstX = useTransform(headingProgress, [0, 1], ['-35%', '0%'])
    const lastX = useTransform(headingProgress, [0, 1], ['35%', '0%'])

    const { scrollYProgress: forkProgress } = useScroll({ target: forkRef, offset: ['start 90%', 'end 60%'] })
    const forkClip = useTransform(forkProgress, [0, 1], ['inset(0% 0% 100% 0%)', 'inset(0% 0% 0% 0%)'])

    const [launch, scale] = paths.options

    useEffect(() => {
        const measure = () => {
            const container = forkRef.current
            const left = launchRef.current
            const right = scaleRef.current

            if (!container || !left || !right) return

            const box = container.getBoundingClientRect()

            if (box.width === 0) {
                setFork(null)

                return
            }

            const leftBox = left.getBoundingClientRect()
            const rightBox = right.getBoundingClientRect()

            setFork({
                width: box.width,
                left: leftBox.left + leftBox.width / 2 - box.left,
                right: rightBox.left + rightBox.width / 2 - box.left
            })
        }

        measure()

        const observer = new ResizeObserver(measure)

        if (forkRef.current) observer.observe(forkRef.current)

        return () => observer.disconnect()
    }, [])

    const middle = fork ? fork.width / 2 : 0
    const branch = (end: number) => `M ${middle} 10 V ${FORK_HEIGHT * 0.36} C ${middle} ${FORK_HEIGHT * 0.8}, ${end} ${FORK_HEIGHT * 0.52}, ${end} ${FORK_END}`

    return (
        <section id='services' className='relative overflow-x-clip bg-paper py-24 sm:py-32 lg:py-40'>
            <div className='shell'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-10'>
                    <h2
                        ref={headingRef}
                        className='font-heading text-[16vw] font-bold leading-[0.86] tracking-[-0.015em] wdth-78 sm:text-[12vw] lg:col-span-8 lg:text-[8.4vw] 2xl:text-[7.75rem]'
                    >
                        <motion.span style={reduce ? undefined : { x: firstX }} className='inline-block'>
                            {paths.heading.first}
                        </motion.span>

                        {' '}

                        <motion.span style={reduce ? undefined : { x: lastX }} className='inline-block'>
                            {paths.heading.second}

                            {' '}

                            <span className='text-blaze-deep'>
                                {paths.heading.third}
                            </span>
                        </motion.span>
                    </h2>

                    <p className='max-w-[30rem] text-lg leading-relaxed text-ink/75 sm:text-xl lg:col-span-4 lg:pb-3'>
                        {paths.intro}
                    </p>
                </div>

                <div ref={forkRef} aria-hidden='true' className='relative mt-12 hidden lg:block' style={{ height: FORK_HEIGHT }}>
                    {fork && (
                        <motion.svg
                            width={fork.width}
                            height={FORK_HEIGHT}
                            viewBox={`0 0 ${fork.width} ${FORK_HEIGHT}`}
                            className='absolute inset-0 overflow-visible'
                            style={reduce ? undefined : { clipPath: forkClip }}
                        >
                            <path d={branch(fork.left)} fill='none' stroke='var(--color-ink)' strokeWidth={3} strokeLinecap='round' strokeDasharray='0 11' />
                            <path d={branch(fork.right)} fill='none' stroke='var(--color-ink)' strokeWidth={3} strokeLinecap='round' strokeDasharray='0 11' />
                            <path d={`M ${middle} 10 V ${FORK_HEIGHT}`} fill='none' stroke='var(--color-ink)' strokeWidth={3} strokeLinecap='round' strokeDasharray='0 11' />

                            <motion.path
                                d={branch(fork.left)}
                                fill='none'
                                stroke='var(--color-blaze)'
                                strokeWidth={5}
                                strokeLinecap='round'
                                initial={false}
                                animate={{ pathLength: hovered === 'launch' ? 1 : 0, opacity: hovered === 'launch' ? 1 : 0 }}
                                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            />

                            <motion.path
                                d={branch(fork.right)}
                                fill='none'
                                stroke='var(--color-cobalt)'
                                strokeWidth={5}
                                strokeLinecap='round'
                                initial={false}
                                animate={{ pathLength: hovered === 'scale' ? 1 : 0, opacity: hovered === 'scale' ? 1 : 0 }}
                                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            />

                            <rect x={middle - 7} y={3} width={14} height={14} rx={2} transform={`rotate(45 ${middle} 10)`} fill='var(--color-ink)' />

                            <motion.circle
                                cx={fork.left}
                                cy={FORK_END}
                                stroke='var(--color-ink)'
                                strokeWidth={2}
                                initial={false}
                                animate={{ r: hovered === 'launch' ? 10 : 6, fill: hovered === 'launch' ? '#22ffb2' : '#0a0a0a' }}
                                transition={{ type: 'spring', stiffness: 500, damping: 18 }}
                            />

                            <motion.circle
                                cx={fork.right}
                                cy={FORK_END}
                                stroke='var(--color-ink)'
                                strokeWidth={2}
                                initial={false}
                                animate={{ r: hovered === 'scale' ? 10 : 6, fill: hovered === 'scale' ? '#02afad' : '#0a0a0a' }}
                                transition={{ type: 'spring', stiffness: 500, damping: 18 }}
                            />
                        </motion.svg>
                    )}
                </div>

                <div className={cn('mt-12 grid gap-4 lg:mt-0 lg:gap-0', SEAM)}>
                    <PathCard
                        option={launch}
                        index={1}
                        cardRef={launchRef}
                        hovered={hovered}
                        onHover={setHovered}
                        className='lg:col-start-1 lg:row-start-1'
                    />

                    <div className='relative hidden lg:col-start-2 lg:row-start-1 lg:flex lg:flex-col lg:items-center lg:justify-center'>
                        <span aria-hidden='true' className='absolute inset-y-0 left-1/2 w-[3px] -translate-x-1/2 [background-image:radial-gradient(circle,var(--color-ink)_1.5px,transparent_1.6px)] [background-size:3px_11px]' />

                        <span aria-hidden='true' className='relative grid size-14 place-items-center rounded-full border-2 border-ink bg-paper font-heading text-lg font-bold'>
                            vs
                        </span>
                    </div>

                    <PathCard
                        option={scale}
                        index={2}
                        cardRef={scaleRef}
                        hovered={hovered}
                        onHover={setHovered}
                        className='lg:col-start-3 lg:row-start-1'
                    />
                </div>

                <div id='path-comparison' className='relative mt-4 overflow-hidden rounded-lg border-2 border-ink lg:mt-0 lg:overflow-visible lg:rounded-none lg:border-0'>
                    <div className='grid grid-cols-2 border-b-2 border-ink font-heading text-base font-bold sm:text-lg lg:hidden'>
                        <p className='bg-blaze px-4 py-3 text-ink'>
                            {launch.title}
                        </p>

                        <p className='border-l-2 border-ink bg-cobalt px-4 py-3 text-ink'>
                            {scale.title}
                        </p>
                    </div>

                    {paths.comparison.map((row, index) => (
                        <ComparisonRow key={row.label} row={row} index={index} first={index === 0} />
                    ))}

                    <div className={cn('hidden lg:grid', SEAM)}>
                        <span className='h-6 rounded-b-lg border-x-2 border-b-2 border-ink bg-blaze-soft' />
                        <span />
                        <span className='h-6 rounded-b-lg border-x-2 border-b-2 border-ink bg-cobalt-soft' />
                    </div>
                </div>

                <div className='mt-14 flex flex-col items-center gap-4 text-center lg:mt-20'>
                    <p className='max-w-[40rem] font-heading text-2xl font-semibold leading-snug tracking-[-0.01em] sm:text-3xl'>
                        {paths.note.before}

                        <QuoteLink
                            path='unsure'
                            className='underline decoration-blaze-deep decoration-[3px] underline-offset-[6px] transition-colors hover:text-blaze-deep'
                        >
                            {paths.note.link}
                        </QuoteLink>

                        {paths.note.after}
                    </p>

                    <SmartLink
                        href='/ai-design-to-website'
                        className='group inline-flex items-center gap-2 text-base font-medium text-ink/75 underline decoration-ink/30 underline-offset-4 transition-colors hover:text-ink hover:decoration-ink sm:text-lg'
                    >
                        {paths.aiLink}
                        <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 ease-snap group-hover:translate-x-1' />
                    </SmartLink>
                </div>
            </div>
        </section>
    )
}

export default Paths
